import { useMutation } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface UploadLoadFileImportParams {
  matterId: string;
  file: File;
}

interface UploadLoadFileImportResult {
  importId: string;
}

interface PresignLoadFileImportResponse {
  url: string;
  fields: Record<string, string>;
  importId: string;
  expiresIn: number;
}

/**
 * Creates the `LoadFileImport` row, fetches a presigned POST, and uploads the `.dat`
 * file directly to S3. Returns the new import id.
 */
export const useUploadLoadFileImport = () => {
  const { fetchConfigGET } = useGetFetchConfig();

  return useMutation<UploadLoadFileImportResult, Error, UploadLoadFileImportParams>(async ({ matterId, file }) => {
    const presignRes = await fetch(
      `${APIBaseChronos}/api/relativity/load-file-import/presigned-post?matterId=${matterId}&fileName=${encodeURIComponent(
        file.name,
      )}`,
      fetchConfigGET,
    );
    if (!presignRes.ok) {
      const errorData = await presignRes.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to prepare upload');
    }

    const { url, fields, importId }: PresignLoadFileImportResponse = await presignRes.json();

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    const s3Res = await fetch(url, { method: 'POST', body: formData });
    if (!s3Res.ok) throw new Error('Failed to upload file to storage');

    return { importId };
  });
};

export default useUploadLoadFileImport;
